/**
 * Canvas 2D 粒子渲染器 — GPU 加速的光流效果
 *
 * 原理：
 * - 用单个 <canvas> 替代 90+ 个 SVG <path> 粒子（30条边 × 3粒子）
 * - Canvas 2D 的 setLineDash + lineDashOffset 在 GPU 合成层上运行
 * - requestAnimationFrame 驱动动画，与 React 渲染周期解耦
 * - 路径几何变化（拖动节点时）不会重置动画相位 → 消除频闪
 * - 流速随缩放幂次补偿（指数 0.45，两端折中微偏原速）：放大画布流速变慢、缩小恢复，
 *   缩放变化时相位按 dt 积分连续推进不跳变
 *
 * 路径注册：
 * - CustomEdge 通过 useEffect 将 edgePath（d 属性字符串）注册到模块级 Map
 * - Canvas 在 rAF 循环中读取该 Map，无需 React 状态更新
 *
 * 性能优化：
 * - memo 包装，防止父组件重渲染牵连
 * - draw 按粒子分组（外层 3 粒子，内层 N 边），状态切换从 90×3 降到 3×3
 * - 遍历 activeEdges 而非 particlePaths，跳过非活跃边
 *
 * 层级（通过 createPortal 渲染到 .react-flow 内部）：
 * - .react-flow__background (z-index: -1) → 不透明背景色，在 Canvas 之下
 * - <canvas> (z-index: 0)                  → 粒子层
 * - .react-flow__pane (z-index: 1)          → 透明事件层，不遮挡
 * - .react-flow__viewport (z-index: 2)     → edges 线 + nodes 覆盖粒子
 *   节点（不透明 div）覆盖粒子 → 粒子在节点背后流动（与原 SVG 粒子效果一致）
 */
import React, { useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { useStore, useStoreApi } from '@xyflow/react';
import { useEdgeActiveStore } from '@/stores/edgeActiveStore';

// ─── 模块级路径注册表 ───────────────────────────────────────────
// 不使用 React 状态 / Zustand，避免 CustomEdge 每帧更新路径时触发重渲染
// Canvas 在 rAF 循环中直接读取此 Map

interface ParticlePath {
  d: string;
  path2d: Path2D;
}

const particlePaths = new Map<string, ParticlePath>();

/** CustomEdge 调用：注册或更新边的路径（d 属性字符串） */
export const setParticlePath = (id: string, d: string) => {
  const existing = particlePaths.get(id);
  if (existing) {
    if (existing.d === d) return; // 路径未变化，跳过 Path2D 重建
    existing.d = d;
    existing.path2d = new Path2D(d);
  } else {
    particlePaths.set(id, { d, path2d: new Path2D(d) });
  }
};

/** CustomEdge 调用：移除边的路径 */
export const removeParticlePath = (id: string) => {
  particlePaths.delete(id);
};

// ─── 粒子配置（与原 SVG 粒子完全一致） ───────────────────────────

// delay 含义（缩放自适应改造后）：不再是动画秒数，粒子拖尾间距 = delay × BASE_SPEED（流单位）
// 0.27 × 700 = 189、0.53 × 700 = 371，与原实现（delay 秒 × 700 单位/秒）的间距完全一致
const PARTICLES = [
  { delay: 0, strokeWidth: 3, opacity: 1 },
  { delay: 0.27, strokeWidth: 2.5, opacity: 0.85 },
  { delay: 0.53, strokeWidth: 2, opacity: 0.7 },
] as const;

const DASH_PATTERN: [number, number] = [40, 520]; // 对应原 strokeDasharray: '40 520'
const DASH_CYCLE = 560; // 40 + 520，对应原 keyframe stroke-dashoffset: 0 → -560

// 缩放自适应流速（幂次补偿）：
// 原实现流速恒定 700 单位/秒（流坐标系），屏幕观感速度 = 700 × zoom，放大看更快、缩小看更慢
// 完全补偿（指数 1，屏幕像素速度恒定）实测高缩放下观感偏慢；不补偿（指数 0）实测高缩放下偏快
// 指数取 0.45（几何中间再偏快一档）：
//   10% 缩放：流速 700（基准，目标观感）
//   100% 缩放：流速 ≈ 248，屏幕 ≈ 248 px/s
//   300% 缩放：流速 ≈ 151，屏幕 ≈ 454 px/s
const BASE_SPEED = 700; // 基准流速（流单位/秒），即 10% 缩放下保持的速度
const SPEED_ANCHOR_ZOOM = 0.1; // 基准缩放：该缩放下流速 = BASE_SPEED
const SPEED_COMPENSATION_EXP = 0.35; // 补偿指数：0=原恒速（高缩放偏快），1=屏幕速度恒定（高缩放偏慢）；越小越快

// ─── Canvas 组件 ───────────────────────────────────────────────

const EdgeParticlesCanvasComponent: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const store = useStoreApi();
  // domNode 是 .react-flow DOM 元素，ReactFlow 初始化后由 store 设置
  const domNode = useStore((s) => s.domNode);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = domNode; // .react-flow 元素
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let rafId = 0;
    let running = true;
    // 缩放自适应流速：dash 偏移按 dt 积分推进（而非全局时钟取模），
    // 缩放变化只改变推进速率、不改变当前偏移 → 相位连续，光流不跳位
    let lastTime = performance.now() / 1000;
    let dashOffset = 0; // 已积累的 dash 偏移（流单位），对 DASH_CYCLE 取模防浮点精度溢出

    // Canvas 尺寸跟随 .react-flow 容器，考虑 devicePixelRatio 保证清晰度
    const resize = () => {
      const rect = container.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const dpr = window.devicePixelRatio || 1;
      const w = Math.floor(rect.width * dpr);
      const h = Math.floor(rect.height * dpr);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
      }
    };

    const ro = new ResizeObserver(resize);
    ro.observe(container);
    resize();

    const draw = () => {
      if (!running) return;

      const dpr = window.devicePixelRatio || 1;
      // 从 ReactFlow store 读取视口变换 [translateX, translateY, zoom]
      // useStoreApi().getState() 不触发重渲染，rAF 循环中直接读取最新值
      const { transform } = store.getState();
      const [tx, ty, tz] = transform;

      // 清除画布（设备像素坐标）
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 应用视口变换 + DPR：
      // screen_x = internal_x * zoom + translate_x
      // Canvas 变换：ctx.setTransform(zoom * dpr, 0, 0, zoom * dpr, tx * dpr, ty * dpr)
      ctx.setTransform(tz * dpr, 0, 0, tz * dpr, tx * dpr, ty * dpr);

      const now = performance.now() / 1000;
      // 按 dt 积分推进 dash 偏移：流速 = BASE_SPEED × (SPEED_ANCHOR_ZOOM / zoom)^指数，随缩放幂次补偿
      // 后台标签页恢复时 rAF 暂停，dt 可能很大 → 钳制到 0.1s，避免恢复瞬间光流跳变
      let dt = now - lastTime;
      lastTime = now;
      if (dt > 0.1) dt = 0.1;
      if (dt < 0) dt = 0;
      const zoom = Math.max(tz, 0.01);
      const flowSpeed = Math.min(
        BASE_SPEED,
        BASE_SPEED * Math.pow(SPEED_ANCHOR_ZOOM / zoom, SPEED_COMPENSATION_EXP)
      );
      dashOffset = (dashOffset + flowSpeed * dt) % DASH_CYCLE;

      // 遍历 activeEdges 而非 particlePaths，跳过非活跃边
      const activeEdges = useEdgeActiveStore.getState().activeEdgeIds;

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#ffffff';
      ctx.setLineDash(DASH_PATTERN);

      // 性能优化：用 Path2D.addPath() 合并所有活跃边的路径，一次 stroke 绘制
      // 之前：30 边 × 3 粒子 = 90 次 ctx.stroke()，每次 stroke 都是独立的 GPU 绘制调用
      // 现在：3 次 ctx.stroke()（每粒子组一次），GPU 绘制调用从 90 降到 3
      // addPath 只是内存复制路径数据，开销极小，远小于 87 次额外的 stroke 调用
      for (let i = 0; i < PARTICLES.length; i++) {
        const particle = PARTICLES[i];
        ctx.globalAlpha = particle.opacity;
        ctx.lineWidth = particle.strokeWidth;
        // 同一粒子的所有边共享相同的 lineDashOffset（同一动画相位）
        // 拖尾间距 = delay × BASE_SPEED 流单位（固定），不随缩放变化
        ctx.lineDashOffset = -(dashOffset + particle.delay * BASE_SPEED);

        // 合并所有活跃边的路径为单个 Path2D
        const combinedPath = new Path2D();
        for (const id of activeEdges) {
          const entry = particlePaths.get(id);
          if (!entry) continue;
          combinedPath.addPath(entry.path2d);
        }
        // 一次 stroke 绘制所有边
        ctx.stroke(combinedPath);
      }

      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      running = false;
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [store, domNode]);

  // domNode 为 null 时（ReactFlow 未初始化）不渲染
  if (!domNode) return null;

  // createPortal 把 Canvas 渲染到 .react-flow 内部
  // 这样 Canvas 在 .react-flow 的 stacking context 内，z-index 与 Background/pane/viewport 同级比较
  return createPortal(
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        // z-index: 0 在 .react-flow 内部
        // - 高于 Background (z-index: -1) → 不被不透明背景遮挡
        // - 低于 pane (z-index: 1) 和 viewport (z-index: 2) → 节点覆盖粒子
        zIndex: 0,
        // GPU 合成层：Canvas 内容更新（stroke）仍需 CPU 光栅化，
        // 但合成在 GPU 上，减少合成开销
        // 点击按钮时 React 渲染阻塞主线程，Canvas 合成层让画面更新不受影响
        willChange: 'transform',
        transform: 'translateZ(0)',
      }}
    />,
    domNode
  );
};

// memo 包装：防止父组件（FlowEditor）重渲染时牵连 EdgeParticlesCanvas 重渲染
// EdgeParticlesCanvas 没有 props，memo 后永远不因父组件重渲染而重渲染
// rAF 循环在 useEffect 中启动，不依赖组件重渲染
export const EdgeParticlesCanvas = memo(EdgeParticlesCanvasComponent);
